import { motion } from "framer-motion";
import { Copy, ExternalLink, Mail } from "lucide-react";
import { toast } from "sonner";
import { identity } from "@/data/portfolio";
import { Section } from "@/components/Section";

const TermLine = ({ cmd, children, testid }) => (
  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2" data-testid={testid}>
    <span className="shrink-0 text-zinc-600">karan@sahu:~$</span>
    <span className="shrink-0 text-cyan-400">{cmd}</span>
    <span className="text-zinc-500">→</span>
    <span className="text-zinc-200">{children}</span>
  </div>
);

export const Contact = () => {
  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(identity.email);
      toast.success("Email copied to clipboard");
    } catch {
      const ta = document.createElement("textarea");
      ta.value = identity.email;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      toast.success("Email copied to clipboard");
    }
  };

  return (
    <Section id="contact" index="06" title="Contact">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.7 }}
        className="scanlines mx-auto max-w-3xl border border-white/15 bg-[#0A0A0A]"
      >
        <div className="flex items-center gap-2 border-b border-white/10 px-5 py-3">
          <span className="h-3 w-3 rounded-full bg-zinc-700" />
          <span className="h-3 w-3 rounded-full bg-zinc-700" />
          <span className="h-3 w-3 rounded-full bg-cyan-400/70" />
          <span className="ml-3 font-mono text-xs text-zinc-500">karan@sahu: ~/contact</span>
        </div>
        <div className="p-6 font-mono text-sm md:p-10">
          <TermLine cmd="whoami" testid="terminal-whoami">{identity.name}</TermLine>
          <TermLine cmd="role" testid="terminal-role">
            {identity.role}
          </TermLine>
          <TermLine cmd="location" testid="terminal-location">{identity.location}</TermLine>
          <TermLine cmd="email" testid="terminal-email">
            <span className="text-cyan-300">{identity.email}</span>
            <button
              data-testid="contact-copy-email-btn"
              onClick={copyEmail}
              className="ml-4 inline-flex items-center gap-1.5 border border-white/20 px-2.5 py-1 text-[10px] uppercase tracking-wider text-zinc-400 transition-colors duration-300 hover:border-cyan-400 hover:text-cyan-400"
            >
              <Copy className="h-3 w-3" /> Copy
            </button>
            <a
              data-testid="contact-mailto-btn"
              href={`mailto:${identity.email}`}
              className="ml-2 inline-flex items-center gap-1.5 border border-cyan-400/60 px-2.5 py-1 text-[10px] uppercase tracking-wider text-cyan-400 transition-colors duration-300 hover:bg-cyan-400 hover:text-black"
            >
              <Mail className="h-3 w-3" /> Mail
            </a>
          </TermLine>
          <TermLine cmd="linkedin" testid="terminal-linkedin">
            <a
              href={identity.links.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-linkedin-link"
              className="inline-flex items-center gap-1.5 text-zinc-200 underline decoration-cyan-400/50 underline-offset-4 transition-colors duration-300 hover:text-cyan-400"
            >
              linkedin.com/in/karansahu1 <ExternalLink className="h-3 w-3" />
            </a>
          </TermLine>
          <TermLine cmd="github" testid="terminal-github">
            <a
              href={identity.links.github}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-github-link"
              className="inline-flex items-center gap-1.5 text-zinc-200 underline decoration-cyan-400/50 underline-offset-4 transition-colors duration-300 hover:text-cyan-400"
            >
              github.com/GameDevKaran <ExternalLink className="h-3 w-3" />
            </a>
          </TermLine>
          <div className="mt-6 flex items-center gap-2 text-zinc-500">
            <span>karan@sahu:~$</span>
            <span className="cursor-blink inline-block h-4 w-2 bg-cyan-400" />
          </div>
        </div>
      </motion.div>
    </Section>
  );
};
